import {
  UX_KINDS,
  UX_SURFACES,
  UX_MAX_DURATION_MS,
} from '../../../packages/domain/src/35-ux-timings.mjs';
/** Aggregate UX timing reports, never browsing histories. Monotonic milestones
 * belong to one signed-in actor and route; failed/stale work emits nothing.
 * Batches are bounded and sent once, without retry or offline persistence. */
export function createUXTimings({
  actor,
  route,
  send,
  platform = globalThis,
  now = () => performance.now(),
}) {
  let owner,
    timer,
    sending = false;
  let events = [];
  let disposed = false;
  const imageWaits = new Set();
  const kinds = new Set(UX_KINDS);
  const surfaces = new Set(UX_SURFACES);
  function align() {
    if (owner !== actor()) {
      owner = actor();
      for (const stop of imageWaits) stop();
      events = [];
      clearTimeout(timer);
      timer = undefined;
    }
  }
  async function flush() {
    clearTimeout(timer);
    timer = undefined;
    align();
    if (sending || !owner || !events.length || platform.document?.hidden) return;
    const batch = events.splice(0, 8);
    sending = true;
    try {
      await send(batch);
    } catch {
      /* Optional aggregate reporting never blocks interaction. */
    } finally {
      sending = false;
      align();
      if (events.length) schedule();
    }
  }
  function schedule() {
    if (!timer) timer = setTimeout(flush, 1000);
  }
  function start(kind, surface, { targetRoute } = {}) {
    align();
    const who = owner,
      path = targetRoute ?? route(),
      began = now();
    let finished = false;
    return () => {
      if (finished || disposed) return;
      finished = true;
      align();
      if (
        !who ||
        who !== owner ||
        path !== route() ||
        platform.document?.hidden ||
        !kinds.has(kind) ||
        !surfaces.has(surface)
      )
        return;
      const durationMs = Math.round(now() - began);
      if (!Number.isFinite(durationMs) || durationMs < 0 || durationMs > UX_MAX_DURATION_MS) return;
      if (events.length >= 16) events.shift();
      events.push({ kind, surface, durationMs });
      schedule();
    };
  }
  function afterPaint(finish) {
    const frame = platform.requestAnimationFrame;
    if (frame) frame(() => frame(finish));
    else finish();
  }
  function afterImage(finish, image) {
    if (!image || imageWaits.size >= 4 || disposed) return;
    const displayed = () =>
      image.isConnected !== false && (!image.getClientRects || image.getClientRects().length > 0);
    if (!displayed()) return;
    if (image.complete) {
      if (image.naturalWidth > 0)
        afterPaint(() => {
          if (displayed()) finish();
        });
      return;
    }
    let timer;
    const stop = () => {
      clearTimeout(timer);
      image.removeEventListener('load', loaded);
      image.removeEventListener('error', stop);
      imageWaits.delete(stop);
    };
    const loaded = () => {
      stop();
      if (image.naturalWidth > 0)
        afterPaint(() => {
          if (displayed()) finish();
        });
    };
    imageWaits.add(stop);
    image.addEventListener('load', loaded, { once: true });
    image.addEventListener('error', stop, { once: true });
    timer = setTimeout(stop, 10000);
  }
  const hide = () => {
    if (platform.document?.hidden) {
      for (const stop of imageWaits) stop();
      events = [];
      clearTimeout(timer);
      timer = undefined;
    }
  };
  platform.document?.addEventListener?.('visibilitychange', hide);
  return {
    start,
    afterPaint,
    afterImage,
    flush,
    dispose() {
      disposed = true;
      for (const stop of imageWaits) stop();
      for (const stop of imageWaits) stop();
      events = [];
      clearTimeout(timer);
      timer = undefined;
      platform.document?.removeEventListener?.('visibilitychange', hide);
    },
  };
}
